import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Copy, RefreshCw } from "lucide-react";

export function magicLinkUrl(token) {
  return `${window.location.origin}/portal/t/${token}`;
}

export function ClientPortalDialog({ clientId, clientName, open, onOpenChange, onChanged }) {
  const [info, setInfo] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !clientId) return;
    api.get(`/clients/${clientId}/portal`).then((r) => {
      setInfo(r.data);
      setEmail(r.data.email || "");
      setPassword("");
    }).catch(() => {});
  }, [open, clientId]);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(`/clients/${clientId}/portal/setup`, {
        email: email || null,
        password: password || null,
      });
      setInfo(data);
      setPassword("");
      toast.success("Portal access saved");
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally { setBusy(false); }
  };

  const regenerate = async () => {
    const { data } = await api.post(`/clients/${clientId}/portal/regenerate-link`);
    setInfo((i) => ({ ...i, magic_token: data.magic_token }));
    toast.success("New link generated — old links no longer work");
  };

  const disable = async () => {
    await api.post(`/clients/${clientId}/portal/disable`);
    setInfo((i) => ({ ...i, enabled: false }));
    toast.success("Portal access disabled");
    onChanged?.();
  };

  const copyLink = () => {
    navigator.clipboard.writeText(magicLinkUrl(info.magic_token));
    toast.success("Link copied — send it to your client");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-xl max-w-md" data-testid="client-portal-dialog">
        <DialogHeader>
          <DialogTitle>Client portal — {clientName}</DialogTitle>
          <DialogDescription className="text-xs">
            Give this client a private link or a login to follow their jobs.
          </DialogDescription>
        </DialogHeader>
        {info?.enabled && info?.magic_token && (
          <div className="rounded-lg border border-zinc-200 dark:border-[#33363B] p-3">
            <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-400 mb-1.5">Magic link (no password needed)</div>
            <div className="flex items-center gap-2">
              <code className="text-[11px] text-zinc-600 dark:text-zinc-300 truncate flex-1" data-testid="portal-magic-link">{magicLinkUrl(info.magic_token)}</code>
              <button onClick={copyLink} className="w-8 h-8 rounded-lg border border-zinc-200 dark:border-[#33363B] flex items-center justify-center text-zinc-500 hover:text-[#FF5F15] shrink-0" data-testid="portal-copy-link" aria-label="Copy link">
                <Copy className="w-3.5 h-3.5" />
              </button>
              <button onClick={regenerate} className="w-8 h-8 rounded-lg border border-zinc-200 dark:border-[#33363B] flex items-center justify-center text-zinc-500 hover:text-[#FF5F15] shrink-0" title="Generate new link" data-testid="portal-regenerate-link" aria-label="Regenerate link">
                <RefreshCw className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        )}
        <form onSubmit={save} className="space-y-3">
          <div>
            <Label className="text-xs font-semibold">Login email (optional)</Label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="client@example.com" className="mt-1.5 rounded-lg" data-testid="portal-setup-email" />
          </div>
          <div>
            <Label className="text-xs font-semibold">{info?.has_password ? "New password (leave blank to keep current)" : "Password (min 8 chars)"}</Label>
            <Input type="text" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="mt-1.5 rounded-lg" data-testid="portal-setup-password" />
          </div>
          <DialogFooter className="gap-2">
            {info?.enabled && (
              <Button type="button" variant="ghost" onClick={disable} className="text-red-600 hover:text-red-700 rounded-lg" data-testid="portal-disable-button">
                Disable access
              </Button>
            )}
            <Button type="submit" disabled={busy} className="rounded-lg bg-[#FF5F15] hover:bg-[#E65100] text-white font-semibold" data-testid="portal-setup-save">
              {busy ? "Saving…" : info?.enabled ? "Save" : "Enable portal"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
